import { getSafeSupabaseClient } from '../../../../lib/supabaseSync';
import type { SystemUser } from '../../../hooks/useSystemUsers';

function looksLikeUuid(value: string): boolean {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value);
}

function fallbackLabel(participantId: string, email?: string | null): string {
  if (email?.includes('@')) {
    const local = email.split('@')[0]?.replace(/[._]/g, ' ').trim();
    if (local) {
      return local
        .split(/\s+/)
        .map(w => w.charAt(0).toUpperCase() + w.slice(1))
        .join(' ');
    }
  }
  if (looksLikeUuid(participantId)) return 'Participant';
  return participantId;
}

/** Merge known display names from workspace users, tool responses, and current user. */
export function buildLiveParticipantNameMap(params: {
  participantIds: string[];
  systemUsers: SystemUser[];
  responseNames: Array<{ participantId: string; participantName?: string }>;
  currentUserId?: string;
  currentUserName?: string;
  extraNames?: Record<string, string>;
}): Record<string, string> {
  const map: Record<string, string> = { ...params.extraNames };

  for (const u of params.systemUsers) {
    if (u.id && u.name) map[u.id] = u.name;
    if (u.externalId && u.name) map[u.externalId] = u.name;
  }

  for (const r of params.responseNames) {
    const trimmed = r.participantName?.trim();
    if (r.participantId && trimmed) map[r.participantId] = trimmed;
  }

  if (params.currentUserId && params.currentUserName?.trim()) {
    map[params.currentUserId] = params.currentUserName.trim();
  }

  for (const id of params.participantIds) {
    if (!map[id] || looksLikeUuid(map[id])) {
      const u = params.systemUsers.find(s => s.id === id || s.externalId === id);
      if (u?.name) map[id] = u.name;
      else if (u?.email) map[id] = fallbackLabel(id, u.email);
    }
  }

  return map;
}

export function resolveLiveParticipantDisplayName(
  participantId: string,
  nameMap: Record<string, string>
): string {
  const hit = nameMap[participantId]?.trim();
  if (hit && !looksLikeUuid(hit)) return hit;
  return fallbackLabel(participantId);
}

/** Fetch public.users names for ids missing from the map (RLS-safe workspace context). */
export async function fetchLiveParticipantNames(
  participantIds: string[],
  existing: Record<string, string>
): Promise<Record<string, string>> {
  const missing = participantIds.filter(id => {
    const n = existing[id]?.trim();
    return !n || looksLikeUuid(n);
  });
  if (missing.length === 0) return {};

  const supabase = getSafeSupabaseClient();
  if (!supabase) return {};

  const { data, error } = await supabase
    .from('users')
    .select('id, external_id, name, email')
    .in('id', missing);

  if (error || !data?.length) return {};

  const out: Record<string, string> = {};
  for (const row of data) {
    const id = row.id as string;
    const name = (row.name as string)?.trim();
    const email = row.email as string | undefined;
    out[id] = name || fallbackLabel(id, email);
    const ext = row.external_id as string | undefined;
    if (ext && out[id]) out[ext] = out[id];
  }
  return out;
}
